import { thingsboardService } from '../../services/thingsboard';
import { SceneRule, SceneAction, SceneCondition } from '../types/scene';

const LOCAL_STORAGE_KEY = 'b2b_smart_scenes';

export const sceneService = {
  /**
   * 1. 시나리오 목록 불러오기 (TENANT 또는 ASSET 속성)
   */
  async getScenes(assetId?: string | null): Promise<SceneRule[]> {
    let allScenes: SceneRule[] = [];

    // 1-1. Try fetching from TENANT SERVER_SCOPE
    try {
      const user = thingsboardService.getCurrentUser?.() || null;
      const tenantId = user?.tenantId?.id;
      if (tenantId) {
        const tenantAttrs = await thingsboardService.getEntityAttributes(
          'TENANT',
          tenantId,
          'SERVER_SCOPE',
          ['b2b_scenes']
        );
        const sceneAttr = tenantAttrs?.find((a: any) => a.key === 'b2b_scenes');
        if (sceneAttr && sceneAttr.value) {
          const parsed = typeof sceneAttr.value === 'string' ? JSON.parse(sceneAttr.value) : sceneAttr.value;
          if (Array.isArray(parsed) && parsed.length > 0) {
            allScenes = parsed;
          }
        }
      }
    } catch (e) {
      console.warn('Could not read from Tenant attributes, falling back to Asset/local:', e);
    }

    // 1-2. Fallback to Asset SERVER_SCOPE
    if (allScenes.length === 0 && assetId && assetId !== '00000000-0000-0000-0000-000000000000') {
      try {
        const attrs = await thingsboardService.getEntityAttributes(
          'ASSET',
          assetId,
          'SERVER_SCOPE',
          ['b2b_scenes']
        );
        const sceneAttr = attrs?.find((a: any) => a.key === 'b2b_scenes');
        if (sceneAttr && sceneAttr.value) {
          const parsed = typeof sceneAttr.value === 'string' ? JSON.parse(sceneAttr.value) : sceneAttr.value;
          if (Array.isArray(parsed)) {
            allScenes = parsed;
          }
        }
      } catch (e) {
        console.warn('Fallback to local storage:', e);
      }
    }

    if (allScenes.length === 0) {
      allScenes = this.getLocalScenes();
    } else {
      this.saveLocalScenes(allScenes);
    }

    // Filter by current apartmentAssetId if specified
    if (assetId && assetId !== '00000000-0000-0000-0000-000000000000') {
      const filtered = allScenes.filter((s) => !s.apartmentAssetId || s.apartmentAssetId === assetId);
      return filtered;
    }

    return allScenes;
  },

  /**
   * 2. 시나리오 저장/수정 (TENANT & ASSET 동시 동기화)
   */
  async saveScene(scene: SceneRule, assetId?: string | null): Promise<{ success: boolean; message: string }> {
    if (assetId) {
      scene.apartmentAssetId = assetId;
    }

    const allScenes = this.getLocalScenes();
    const idx = allScenes.findIndex((s) => s.id === scene.id);

    if (idx >= 0) {
      allScenes[idx] = scene;
    } else {
      allScenes.unshift(scene);
    }

    this.saveLocalScenes(allScenes);

    // Save to TENANT SERVER_SCOPE for 1-minute Rule Engine
    try {
      const user = thingsboardService.getCurrentUser?.() || null;
      const tenantId = user?.tenantId?.id;
      if (tenantId) {
        await thingsboardService.saveEntityAttributes('TENANT', tenantId, 'SERVER_SCOPE', {
          b2b_scenes: JSON.stringify(allScenes),
        });
      }
    } catch (e) {
      console.warn('Failed to save to Tenant attributes:', e);
    }

    // Also Save to ASSET SERVER_SCOPE
    if (assetId && assetId !== '00000000-0000-0000-0000-000000000000') {
      try {
        await thingsboardService.saveEntityAttributes('ASSET', assetId, 'SERVER_SCOPE', {
          b2b_scenes: JSON.stringify(allScenes.filter(s => !s.apartmentAssetId || s.apartmentAssetId === assetId)),
        });
      } catch (e) {
        console.warn('Failed to save to Asset attributes:', e);
      }
    }

    return {
      success: true,
      message: `'${scene.name}' 시나리오가 성공적으로 저장되었습니다.`,
    };
  },

  /**
   * 3. 시나리오 삭제 (TENANT & ASSET 동시 삭제)
   */
  async deleteScene(sceneId: string, assetId?: string | null): Promise<boolean> {
    const allScenes = this.getLocalScenes().filter((s) => s.id !== sceneId);
    this.saveLocalScenes(allScenes);

    // Sync to TENANT
    try {
      const user = thingsboardService.getCurrentUser?.() || null;
      const tenantId = user?.tenantId?.id;
      if (tenantId) {
        await thingsboardService.saveEntityAttributes('TENANT', tenantId, 'SERVER_SCOPE', {
          b2b_scenes: JSON.stringify(allScenes),
        });
      }
    } catch (e) {
      console.warn('Failed to delete from Tenant attributes:', e);
    }

    // Sync to ASSET
    if (assetId && assetId !== '00000000-0000-0000-0000-000000000000') {
      try {
        await thingsboardService.saveEntityAttributes('ASSET', assetId, 'SERVER_SCOPE', {
          b2b_scenes: JSON.stringify(allScenes.filter(s => !s.apartmentAssetId || s.apartmentAssetId === assetId)),
        });
      } catch (e) {
        console.warn('Failed to delete from Asset attributes:', e);
      }
    }
    return true;
  },

  /**
   * 4. 시나리오 활성화/비활성화 토글
   */
  async toggleSceneEnable(sceneId: string, enabled: boolean, assetId?: string | null): Promise<boolean> {
    const allScenes = this.getLocalScenes();
    const target = allScenes.find((s) => s.id === sceneId);
    if (target) {
      target.enabled = enabled;
      await this.saveScene(target, assetId);
      return true;
    }
    return false;
  },

  /**
   * 6. 시나리오 즉시 실행 (Run Now)
   */
  async executeScene(scene: SceneRule): Promise<{ success: boolean; message: string }> {
    let successCount = 0;
    let failCount = 0;

    for (const action of scene.actions) {
      try {
        if (action.type === 'device_control' && action.deviceControl) {
          const { deviceIds, deviceTypes, command, modeValue } = action.deviceControl;

          for (let i = 0; i < deviceIds.length; i++) {
            const devId = deviceIds[i];
            const devType = deviceTypes[i] || 'SEO';

            let method = 'set_plug';
            let params: any = { value: 'on' };

            if (devType === 'SEL') {
              method = 'set_light';
              if (command === 'turn_on') params = { state: true };
              else if (command === 'turn_off') params = { state: false };
              else if (command === 'set_mode') params = { mode: modeValue };
            } else if (devType === 'SES') {
              method = 'set_switch';
              if (command === 'turn_on') params = { value: 'on' };
              else if (command === 'turn_off') params = { value: 'off' };
            } else if (devType === 'SER') {
              method = 'set_relay';
              if (command === 'turn_on') params = { value: 'on' };
              else if (command === 'turn_off') params = { value: 'off' };
            } else {
              // SEO, SEM or default
              method = 'set_plug';
              if (command === 'turn_on') params = { value: 'on' };
              else if (command === 'turn_off') params = { value: 'off' };
              else if (command === 'set_mode') params = { mode: modeValue };
            }

            try {
              await thingsboardService.sendRpcRequest(devId, method, params);
              successCount++;
            } catch (err) {
              console.warn(`RPC failed for device ${devId}:`, err);
              failCount++;
            }
          }
        } else if (action.type === 'notification') {
          console.log(`[Notification Action] ${action.notification?.title}: ${action.notification?.message}`);
          successCount++;
        }
      } catch (err) {
        console.error('Failed to execute action:', action, err);
        failCount++;
      }
    }

    if (failCount === 0 && successCount > 0) {
      return { success: true, message: `'${scene.name}' 시나리오가 성공적으로 실행되었습니다.` };
    } else if (successCount > 0) {
      return {
        success: true,
        message: `'${scene.name}' 시나리오: ${successCount}개 동작 성공, ${failCount}개 실패`,
      };
    } else {
      return { success: false, message: `'${scene.name}' 시나리오 실행 중 오류가 발생했습니다.` };
    }
  },

  // ─── Local Storage Cache Helpers ───
  getLocalScenes(): SceneRule[] {
    try {
      const data = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!data) return [];
      return JSON.parse(data);
    } catch {
      return [];
    }
  },

  saveLocalScenes(scenes: SceneRule[]): void {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(scenes));
    } catch (e) {
      console.error('Failed to write scenes to local storage', e);
    }
  },
};
